"use client";
import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import axios from "axios";
import Modal from "react-modal";
import {
  Loader2,
  Search,
  Plus,
  Upload,
  Trash2,
  AlertCircle,
  CheckCircle,
  FileSpreadsheet,
  Filter,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import PropTypes from "prop-types";
import ExcelUploadWizard from "../../ExcelUploadWizard/ExcelUploadWizard";

function SchemaDrivenTable({ records, schema, onDelete }) {
  if (!records.length) return null;
  return (
    <div className="overflow-x-auto shadow-lg rounded-lg border border-gray-800">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-gray-800">
          <tr>
            {schema.map((col) => (
              <th
                key={col}
                className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap"
              >
                {col}
              </th>
            ))}
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {records.map((doc) => (
            <tr
              key={doc._id}
              className="odd:bg-gray-900 even:bg-gray-850 hover:bg-gray-800 transition-colors"
            >
              {schema.map((col) => (
                <td key={col} className="p-3 border-b border-gray-800">
                  {doc[col] !== undefined ? String(doc[col]) : ""}
                </td>
              ))}
              <td className="p-3 border-b border-gray-800">
                <button
                  onClick={() => onDelete(doc._id)}
                  className="bg-red-600 hover:bg-red-700 transition-colors duration-200 px-3 py-1.5 rounded-lg flex items-center gap-1"
                  title="Delete record"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

SchemaDrivenTable.propTypes = {
  records: PropTypes.array.isRequired,
  schema: PropTypes.arrayOf(PropTypes.string).isRequired,
  onDelete: PropTypes.func.isRequired,
};

Modal.setAppElement("#root");

export default function IronworkersDashboard() {
  const { auth } = useContext(AuthContext);
  const [subscription, setSubscription] = useState(null);
  const isSubscriptionInactive = subscription?.status === "inactive";

  const [userSchema, setUserSchema] = useState([]);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 50;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newRecord, setNewRecord] = useState({});

  // Hook: fetch subscription details
  useEffect(() => {
    if (!auth?.token) return;
    fetch("/api/myteconstruction/organization/me", {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.organization?.subscription) {
          setSubscription(data.organization.subscription);
        }
      })
      .catch((err) => console.error("org sub error:", err));
  }, [auth.token]);

  // Define fetchRecords using useCallback (stable dependency)
  const fetchRecords = useCallback(async () => {
    setStatusMessage("Loading data...");
    try {
      const trimmedSearch = searchTerm.trim();
      const resp = await axios.get("/api/myteconstruction/ironworkers", {
        headers: { Authorization: `Bearer ${auth.token}` },
        params: { search: trimmedSearch, page, limit },
      });
      setRecords(resp.data.data || []);
      setTotalPages(resp.data.totalPages || 1);
      setStatusMessage("");
    } catch (err) {
      console.error("Fetch records error:", err);
      setStatusMessage("Failed to fetch data.");
    }
  }, [auth.token, searchTerm, page]);

  // Hook: fetch records on mount and page/search change
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Hook: derive schema from records
  useEffect(() => {
    if (!records.length || userSchema.length) return;
    const allKeys = new Set();
    for (const doc of records) {
      Object.keys(doc).forEach((k) => {
        if (!["_id", "userId", "createdAt", "updatedAt", "__v"].includes(k)) {
          allKeys.add(k);
        }
      });
    }
    setUserSchema([...allKeys]);
  }, [records, userSchema]);

  // Return UI early after hooks
  if (!auth?.token) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-4 flex items-center justify-center">
        <div className="bg-gray-900 p-6 rounded-lg shadow-lg border border-gray-800 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
          <p className="text-lg font-medium">Please log in to view Ironworkers.</p>
        </div>
      </div>
    );
  }

  // Handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleCreateRecord = async () => {
    try {
      await axios.post("/api/myteconstruction/ironworkers", newRecord, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      setStatusMessage("Record created successfully!");
      setNewRecord({});
      setIsCreateModalOpen(false);
      fetchRecords();
    } catch (err) {
      console.error("Create record error:", err);
      setStatusMessage("Failed to create record.");
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm("Are you sure to delete?")) return;
    try {
      await axios.delete(`/api/myteconstruction/ironworkers/${id}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      setStatusMessage("Record deleted.");
      fetchRecords();
    } catch (err) {
      console.error("Delete record error:", err);
      setStatusMessage("Failed to delete.");
    }
  };

  const subscriptionAlert = isSubscriptionInactive
    ? "Your subscription is expired/inactive. Please upgrade."
    : "";

  return (
    <div className="bg-gray-950 text-white min-h-screen flex flex-col">
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row items-center justify-between bg-gray-900 p-3 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        {subscriptionAlert && (
          <div className="bg-red-600 text-white px-3 py-1 rounded-md shadow-md text-sm mb-2 md:mb-0 mr-4 flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4" />
            {subscriptionAlert}{" "}
            <a
              href="/myteconstruction/upgrade"
              className="underline hover:text-white ml-1"
            >
              Upgrade
            </a>
          </div>
        )}
        <div className="w-full md:w-auto mb-2 md:mb-0 flex items-center gap-2">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              className="bg-gray-800 text-white rounded-lg pl-10 pr-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="Search ironworkers..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <button
            className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors"
            title="Filter records"
          >
            <Filter className="h-4 w-4 text-gray-300" />
          </button>
          <button
            className="bg-gray-800 p-2 rounded-lg hover:bg-gray-700 transition-colors"
            title="Refresh data"
            onClick={fetchRecords}
          >
            <RefreshCw className="h-4 w-4 text-gray-300" />
          </button>
        </div>
        <div className="flex space-x-2">
          <button
            className="bg-blue-600 px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-md disabled:opacity-50 text-sm font-medium"
            onClick={() => setIsCreateModalOpen(true)}
            disabled={isSubscriptionInactive}
          >
            <Plus className="h-4 w-4" />
            <span>Add Record</span>
          </button>
          <button
            className="bg-purple-600 px-3 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2 shadow-md disabled:opacity-50 text-sm font-medium"
            onClick={() => setIsUploadModalOpen(true)}
            disabled={isSubscriptionInactive}
          >
            <Upload className="h-4 w-4" />
            <span>Bulk Upload</span>
          </button>
          <button
            className="bg-green-600 px-3 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2 shadow-md disabled:opacity-50 text-sm font-medium"
            disabled={!records.length || isSubscriptionInactive}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 flex-1">
        {statusMessage && (
          <div className="mb-4 bg-gray-800 p-3 rounded-lg text-sm flex items-center gap-2 border-l-4 border-blue-500 shadow-md">
            {statusMessage.includes("Loading") ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
            ) : statusMessage.includes("success") ? (
              <CheckCircle className="h-4 w-4 text-green-400" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-400" />
            )}
            <span>{statusMessage}</span>
          </div>
        )}

        <SchemaDrivenTable
          records={records}
          schema={[...userSchema]}
          onDelete={handleDeleteRecord}
        />

        {!records.length && !statusMessage && (
          <div className="mt-4 p-6 bg-gray-800 rounded-lg text-center border border-gray-700 shadow-md">
            <FileSpreadsheet className="h-12 w-12 mx-auto mb-2 text-gray-400" />
            <p className="mb-2 text-lg font-medium">No records found</p>
            <p className="text-sm text-gray-400">
              Try uploading data or adjusting your search
            </p>
          </div>
        )}

        {records.length > 0 && (
          <div className="flex items-center justify-center mt-6 space-x-4">
            <button
              onClick={handlePrevPage}
              disabled={page <= 1}
              className="bg-gray-800 px-3 py-2 rounded-lg disabled:opacity-50 hover:bg-gray-700 flex items-center gap-1 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Prev</span>
            </button>
            <span className="text-gray-300 bg-gray-800 px-4 py-2 rounded-lg">
              Page <span className="font-medium">{page}</span> of{" "}
              <span className="font-medium">{totalPages}</span>
            </span>
            <button
              onClick={handleNextPage}
              disabled={page >= totalPages}
              className="bg-gray-800 px-3 py-2 rounded-lg disabled:opacity-50 hover:bg-gray-700 flex items-center gap-1 transition-colors"
            >
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onRequestClose={() => setIsCreateModalOpen(false)}
        className="bg-gray-900 text-white p-6 rounded-lg max-w-md mx-auto mt-20 w-full border border-gray-700 shadow-xl"
        overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Record</h2>
          <button
            onClick={() => setIsCreateModalOpen(false)}
            className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {!userSchema.length ? (
          <div className="text-sm mb-4 p-4 bg-gray-800 rounded-lg text-gray-400 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            <p>No columns detected yet. Upload or refresh to see dynamic fields.</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {userSchema.map((col) => (
              <div key={col}>
                <label className="block text-sm mb-1 text-gray-300">{col}:</label>
                <input
                  type="text"
                  className="w-full bg-gray-800 p-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  value={newRecord[col] || ""}
                  onChange={(e) =>
                    setNewRecord((prev) => ({
                      ...prev,
                      [col]: e.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setIsCreateModalOpen(false)}
            className="bg-gray-700 px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateRecord}
            className="bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            disabled={isSubscriptionInactive}
          >
            <Plus className="h-4 w-4" />
            <span>Save</span>
          </button>
        </div>
      </Modal>

      {/* Bulk Upload Modal */}
      {isUploadModalOpen && (
        <Modal
          isOpen={isUploadModalOpen}
          onRequestClose={() => setIsUploadModalOpen(false)}
          className="bg-gray-900 text-white p-6 rounded-lg max-w-2xl mx-auto mt-20 w-full border border-gray-700 shadow-xl"
          overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
        >
          <ExcelUploadWizard
            userSchema={userSchema}
            setUserSchema={setUserSchema}
            onClose={() => setIsUploadModalOpen(false)}
            onSuccess={() => {
              setIsUploadModalOpen(false);
              fetchRecords();
            }}
          />
        </Modal>
      )}
    </div>
  );
}
